import React, { useEffect, useState } from "react";
import { t, tf } from "../i18n";
import { overlayMotionProps, dialogMotionProps } from "../utils/modalPresence";
import {
  XIcon,
  ImageIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  StarIcon,
  PackageIcon,
  MapPinIcon,
  UserIcon,
  ClockIcon,
  FileIcon,
  SendIcon,
  AlertIcon
} from "./Icons";

// Предпросмотр объявления перед публикацией: как его увидит покупатель —
// фото, цена, название, где и у кого, описание и параметры. Оформлен как
// остальные окна панели: цвета из темы, шапка с крестиком, подвал с теми же
// кнопками, что у формы. Фото — в тёмном просмотрщике (на светлом фоне снимок
// не оценить) и целиком, без обрезки: так, как их покажет Kleinanzeigen.
//
// Прокручивается только середина (data-modal-scroll — её пропускает защита
// касаний окна публикации); шапка и подвал стоят на месте.

const AdPreviewDialog = ({
  presenceState,
  isPhone = false,
  photos = [],
  price = "",
  priceType = "FIXED",
  title = "",
  categoryPath = "",
  postalCode = "",
  city = "",
  accountName = "",
  accountEmail = "",
  scheduledAt = "",
  scheduledLabel = "",
  description = "",
  params = [],
  missingLabel = "",
  publishing = false,
  onClose,
  onSaveDraft,
  onPublish
}) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const count = photos.length;
  const current = photos[Math.min(photoIndex, Math.max(0, count - 1))] || null;
  useEffect(() => {
    if (photoIndex > 0 && photoIndex >= count) setPhotoIndex(Math.max(0, count - 1));
  }, [count, photoIndex]);
  const step = (delta) => {
    if (count < 2) return;
    setPhotoIndex((index) => (index + delta + count) % count);
  };

  const priceText = String(price || "").trim();
  const negotiable = String(priceType || "").toUpperCase() === "NEGOTIABLE";
  const titleText = String(title || "").trim();
  const descriptionText = String(description || "").trim();
  const location = [String(postalCode || "").trim(), String(city || "").trim()].filter(Boolean).join(" ");

  const facts = [
    { key: "category", icon: <PackageIcon size={16} />, label: t("Категория"), value: categoryPath || t("Не выбрана"), empty: !categoryPath },
    { key: "location", icon: <MapPinIcon size={16} />, label: t("Где"), value: location || t("PLZ не указан"), empty: !location },
    {
      key: "seller",
      icon: <UserIcon size={16} />,
      label: t("Продавец"),
      value: accountName || t("Аккаунт не выбран"),
      sub: accountEmail,
      empty: !accountName
    },
    {
      key: "when",
      icon: <ClockIcon size={16} />,
      label: t("Публикация"),
      value: scheduledAt ? tf`Запланирована на ${scheduledLabel}` : t("Сразу после нажатия «Опубликовать»"),
      warn: Boolean(scheduledAt)
    }
  ];

  return (
    <div
      {...overlayMotionProps(presenceState)}
      className="avp-overlay"
      onClick={onClose}
    >
      <div
        {...dialogMotionProps(presenceState)}
        className={`avp-window${isPhone ? " is-phone" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={t("Предпросмотр объявления")}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="avp-head">
          <div style={{ minWidth: 0 }}>
            <div className="avp-head-title">{t("Предпросмотр")}</div>
            <div className="avp-head-sub">{t("Так объявление увидят на Kleinanzeigen")}</div>
          </div>
          <button type="button" className="avp-icon-btn" onClick={onClose} aria-label={t("Закрыть")} title={t("Закрыть")}>
            <XIcon size={16} />
          </button>
        </div>

        <div className="avp-body" data-modal-scroll="1">
          <div className="avp-grid">
            <div className="avp-gallery">
              <div className="avp-stage">
                {current ? (
                  <>
                    <img className="avp-stage-backdrop" src={current.url} alt="" aria-hidden="true" />
                    <img className="avp-stage-img" src={current.url} alt={titleText || t("Фото объявления")} />
                    {photoIndex === 0 && (
                      <span className="avp-stage-chip is-cover"><StarIcon size={11} fill="currentColor" />{t("Обложка")}</span>
                    )}
                    {count > 1 && (
                      <>
                        <span className="avp-stage-chip is-count">{photoIndex + 1} / {count}</span>
                        <button type="button" className="avp-stage-nav is-prev" onClick={() => step(-1)} aria-label={t("Предыдущее фото")}>
                          <ChevronLeftIcon size={18} />
                        </button>
                        <button type="button" className="avp-stage-nav is-next" onClick={() => step(1)} aria-label={t("Следующее фото")}>
                          <ChevronRightIcon size={18} />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="avp-stage-empty">
                    <ImageIcon size={30} />
                    <span>{t("Фото пока не добавлены")}</span>
                  </div>
                )}
              </div>
              {count > 1 && (
                <div className="avp-thumbs">
                  {photos.map((photo, index) => (
                    <button
                      key={photo.key}
                      type="button"
                      className={`avp-thumb${index === photoIndex ? " is-on" : ""}`}
                      onClick={() => setPhotoIndex(index)}
                      aria-label={tf`Фото ${index + 1}`}
                    >
                      <img src={photo.url} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="avp-info">
              <div className="avp-price-row">
                {priceText
                  ? <span className="avp-price">{priceText} €</span>
                  : <span className="avp-price is-empty">{t("Цена не указана")}</span>}
                <span className="avp-chip">{negotiable ? "VB" : "Festpreis"}</span>
              </div>
              <h3 className={`avp-title${titleText ? "" : " is-empty"}`}>{titleText || t("Без названия")}</h3>
              <div className="avp-facts">
                {facts.map((fact) => (
                  <div key={fact.key} className="avp-fact">
                    <span className="avp-fact-icon">{fact.icon}</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="avp-fact-label">{fact.label}</div>
                      <div className={`avp-fact-value${fact.empty ? " is-empty" : ""}${fact.warn ? " is-warn" : ""}`}>{fact.value}</div>
                      {fact.sub && <div className="avp-fact-sub">{fact.sub}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="avp-section avp-wide">
              <div className="avp-section-title">{t("Описание")}</div>
              <div className={`avp-description${descriptionText ? "" : " is-empty"}`}>
                {descriptionText || t("Описание не заполнено")}
              </div>
            </div>

            {params.length > 0 && (
              <div className="avp-section avp-wide">
                <div className="avp-section-title">{t("Параметры")}</div>
                <dl className="avp-params">
                  {params.map((item) => (
                    <div key={`${item.label}:${item.value}`} className="avp-param">
                      <dt>{item.label}</dt>
                      <dd>{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>

        <div className="avp-foot">
          {missingLabel && (
            <div className="avp-missing">
              <AlertIcon size={15} />
              <span>{missingLabel}</span>
            </div>
          )}
          <div className="avp-actions">
            <button type="button" className="avp-btn" onClick={onSaveDraft}>
              <FileIcon size={15} />
              {t("В черновик")}
            </button>
            <button type="button" className="avp-btn is-primary" onClick={onPublish} disabled={publishing}>
              <SendIcon size={15} />
              {publishing ? t("Публикуем...") : t("Опубликовать")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdPreviewDialog;
